import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, Fragment as _Fragment, renderList as _renderList, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "exam-history" }
const _hoisted_2 = { class: "exam-history__head" }
const _hoisted_3 = { class: "exam-history__title" }
const _hoisted_4 = {
  class: "exam-history__facts",
  "aria-label": "これまでの受験"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "exam-history__retry-options" }
const _hoisted_8 = {
  class: "exam-history__list",
  "aria-label": "受験の記録"
}
const _hoisted_9 = { class: "exam-history__no" }
const _hoisted_10 = { class: "exam-history__main" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { class: "exam-history__result" }
const _hoisted_14 = {
  key: 0,
  class: "exam-history__chip is-wait"
}
const _hoisted_15 = {
  key: 1,
  class: "exam-history__chip is-wait"
}
const _hoisted_16 = ["disabled", "onClick"]
const _hoisted_17 = ["onClick"]
const _hoisted_18 = {
  key: 3,
  class: "exam-history__spacer",
  "aria-hidden": "true"
}

import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import flatten from 'lodash/flatten'
import DialogDeleteConfirm from '../atoms_molecules/DialogDeleteConfirm.vue'
import { useExam } from './useExam'
import Exam from '../../models/exam'
import UserExam from '../../models/user_exam'
import { ROUTER_NAME } from '../../router'
import SchoolSetting from '../../../api/v2/models/school_setting'

interface Row { exam: Exam; userExam: UserExam; label: string; done: boolean }


export default /*@__PURE__*/_defineComponent({
  __name: 'ExamHistoryView',
  props: {
    exam: {},
    list: { default: () => [] },
    schoolSetting: {}
  },
  emits: ["fetchList"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const router = useRouter()
const { t } = useI18n()
const { TYPE_USE_NIGATE_EXAM, TYPE_USE_SPECIAL_EXAM, TYPE_USE_EXAM_FROM_FAILED, gotoExam, reTest, confimClearHistoryTest } = useExam()

// 再テストで作られたテスト（間違えた問題だけ、など）は、元のテストの子として一覧に入っている
const children = (exam: Exam): Exam[] => {
  const direct = props.list.filter(item => item.parentId == exam.id)
  return flatten([direct, ...direct.map(children)])
}

// 古い順：最初の受験が一番上で、そこから再テストが順に並ぶ
const rows = computed<Row[]>(() => {
  const all = [props.exam, ...children(props.exam)].flatMap(exam => exam.userExams.map(userExam => ({ exam, userExam })))
  all.sort((a, b) => new Date(a.userExam.createdAt).getTime() - new Date(b.userExam.createdAt).getTime() || a.userExam.id - b.userExam.id)
  return all.map(({ exam, userExam }, i) => ({ exam, userExam, done: !!exam.isDone(userExam), label: i === 0 ? '最初の受験' : `再テスト${i}（${kind(exam)}）` }))
})
const kind = (exam: Exam) => {
  if (exam.id === props.exam.id) return 'すべての問題'
  if (exam.examType === Exam.TYPE_USE_EXAM_FROM_FAILED) return '間違えた問題だけ'
  if (exam.examType === Exam.TYPE_USE_NIGATE_EXAM) return 'ニガテ問題'
  if (exam.examType === Exam.TYPE_USE_SPECIAL_EXAM) return '別の問題'
  return '再テスト'
}
const latest = computed(() => rows.value[rows.value.length - 1])
const finished = computed(() => rows.value.filter(row => row.done))
const best = computed(() => {
  if (props.exam.hideResult) return null
  const scores = finished.value.filter(row => !row.userExam.isWaitingScoreCheck).map(row => Math.round(Number(row.userExam.average ?? 0)))
  return scores.length ? Math.max(...scores) : null
})

const dateFormat = (date: Date) => format(date, 'YYYY/MM/DD HH:mm')
const btnName = (exam: Exam, userExam: UserExam) => {
  if (!exam.isStarted(userExam)) return t('exam_list.start') as string
  if (exam.isTestTimeOver(userExam)) return t('exam_list.time_over') as string
  return t('exam_list.continue') as string
}
const open = (row: Row) => {
  if (row.userExam.isWaitingScoreCheck) return
  router.push({ name: ROUTER_NAME.EXAM_RESULT, params: { examId: row.exam.id.toString() }, query: { user_exam_id: row.userExam.id.toString() } })
}
const retryMenu = ref<HTMLDetailsElement | null>(null)
const retry = (exam: Exam, examType = '') => {
  if (retryMenu.value) retryMenu.value.open = false
  reTest(exam, examType)
}
const remove = (userExam: UserExam) => confimClearHistoryTest(userExam, emit)

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createCommentVNode(" 1つのテストの「今までの結果」。上に概要とやり直し、下に受験の記録を古い順（最初の受験 → 再テスト1 → …）に並べる "),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "exam-history__eyebrow" }, "今までのテスト結果", -1 /* CACHED */)),
          _createElementVNode("h1", null, _toDisplayString(__props.exam.name), 1 /* TEXT */),
          _createElementVNode("ul", _hoisted_4, [
            _createElementVNode("li", null, [
              _createVNode(_component_v_icon, { size: "16" }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("mdi-counter", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }),
              _createTextVNode("受験 " + _toDisplayString(finished.value.length) + "回", 1 /* TEXT */)
            ]),
            (best.value !== null)
              ? (_openBlock(), _createElementBlock("li", _hoisted_5, [
                  _createVNode(_component_v_icon, { size: "16" }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode("mdi-trophy-outline", -1 /* CACHED */)
                    ]))]),
                    _: 1 /* STABLE */
                  }),
                  _createTextVNode("最高 " + _toDisplayString(best.value) + "点", 1 /* TEXT */)
                ]))
              : _createCommentVNode("v-if", true),
            (__props.exam.passScore)
              ? (_openBlock(), _createElementBlock("li", _hoisted_6, [
                  _createVNode(_component_v_icon, { size: "16" }, {
                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                      _createTextVNode("mdi-flag-checkered", -1 /* CACHED */)
                    ]))]),
                    _: 1 /* STABLE */
                  }),
                  _createTextVNode("合格 " + _toDisplayString(__props.exam.passScore) + "点", 1 /* TEXT */)
                ]))
              : _createCommentVNode("v-if", true)
          ])
        ]),
        (__props.exam.canRetest && latest.value && latest.value.done)
          ? (_openBlock(), _createElementBlock("details", {
              key: 0,
              ref_key: "retryMenu",
              ref: retryMenu,
              class: "exam-history__retry"
            }, [
              _cache[12] || (_cache[12] = _createElementVNode("summary", null, [
                _createTextVNode("もう一度解く "),
                _createElementVNode("span", { "aria-hidden": "true" }, "⌄")
              ], -1 /* CACHED */)),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (retry(latest.value.exam, _unref(TYPE_USE_EXAM_FROM_FAILED).toString())))
                }, [...(_cache[8] || (_cache[8] = [
                  _createTextVNode("間違えた問題だけ", -1 /* CACHED */),
                  _createElementVNode("small", null, "前回まちがえた問題から、あなた専用のテストを作ります", -1 /* CACHED */)
                ]))]),
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (retry(__props.exam)))
                }, [...(_cache[9] || (_cache[9] = [
                  _createTextVNode("すべての問題", -1 /* CACHED */),
                  _createElementVNode("small", null, "同じテストを最初から解き直します", -1 /* CACHED */)
                ]))]),
                (__props.schoolSetting.useExamStock)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("button", {
                        type: "button",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (retry(latest.value.exam, _unref(TYPE_USE_NIGATE_EXAM).toString())))
                      }, [...(_cache[10] || (_cache[10] = [
                        _createTextVNode("ニガテ問題", -1 /* CACHED */),
                        _createElementVNode("small", null, "苦手な分野から出題します", -1 /* CACHED */)
                      ]))]),
                      _createElementVNode("button", {
                        type: "button",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (retry(__props.exam, _unref(TYPE_USE_SPECIAL_EXAM).toString())))
                      }, [...(_cache[11] || (_cache[11] = [
                        _createTextVNode("別の問題", -1 /* CACHED */),
                        _createElementVNode("small", null, "同じ範囲の、別の問題で解きます", -1 /* CACHED */)
                      ]))])
                    ], 64 /* STABLE_FRAGMENT */))
                  : _createCommentVNode("v-if", true)
              ])
            ], 512 /* NEED_PATCH */))
          : _createCommentVNode("v-if", true)
      ]),
      _createElementVNode("section", _hoisted_8, [
        _cache[16] || (_cache[16] = _createElementVNode("h2", null, "受験の記録", -1 /* CACHED */)),
        _createElementVNode("ol", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, i) => {
            return (_openBlock(), _createElementBlock("li", {
              key: row.userExam.id,
              class: _normalizeClass({ 'is-latest': row === latest.value })
            }, [
              _createElementVNode("span", _hoisted_9, _toDisplayString(i + 1), 1 /* TEXT */),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("b", null, [
                  _createTextVNode(_toDisplayString(row.label), 1 /* TEXT */),
                  (row === latest.value && rows.value.length > 1)
                    ? (_openBlock(), _createElementBlock("em", _hoisted_11, "最新"))
                    : _createCommentVNode("v-if", true)
                ]),
                (row.userExam.startedAt)
                  ? (_openBlock(), _createElementBlock("small", _hoisted_12, _toDisplayString(dateFormat(row.userExam.startedAt)), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("div", _hoisted_13, [
                (row.done)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (__props.exam.hideResult)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_14, "結果公開待ち"))
                        : (row.userExam.isWaitingScoreCheck)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_15, "採点待ち"))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createElementVNode("strong", null, [
                                _createTextVNode(_toDisplayString(Math.round(Number(row.userExam.average ?? 0))), 1 /* TEXT */),
                                _cache[13] || (_cache[13] = _createElementVNode("small", null, "点", -1 /* CACHED */))
                              ]),
                              (__props.exam.passScore)
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass(["exam-history__chip", row.userExam.passed ? 'is-pass' : 'is-fail'])
                                  }, _toDisplayString(row.userExam.passed ? '合格' : '不合格'), 3 /* TEXT, CLASS */))
                                : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */)),
                      _createElementVNode("button", {
                        type: "button",
                        class: "exam-history__open",
                        disabled: row.userExam.isWaitingScoreCheck,
                        onClick: ($event: any) => (open(row))
                      }, [...(_cache[14] || (_cache[14] = [
                        _createTextVNode("結果を見る ", -1 /* CACHED */),
                        _createElementVNode("span", { "aria-hidden": "true" }, "→", -1 /* CACHED */)
                      ]))], 8 /* PROPS */, _hoisted_16)
                    ], 64 /* STABLE_FRAGMENT */))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      type: "button",
                      class: "exam-history__open is-primary",
                      onClick: ($event: any) => (_unref(gotoExam)(row.exam, row.userExam))
                    }, [
                      _createTextVNode(_toDisplayString(btnName(row.exam, row.userExam)) + " ", 1 /* TEXT */),
                      _cache[15] || (_cache[15] = _createElementVNode("span", { "aria-hidden": "true" }, "→", -1 /* CACHED */))
                    ], 8 /* PROPS */, _hoisted_17)),
                (i > 0)
                  ? (_openBlock(), _createBlock(DialogDeleteConfirm, {
                      key: 2,
                      title: "本当にテストを削除しますか？",
                      onConfirmed: ($event: any) => (remove(row.userExam))
                    }, null, 8 /* PROPS */, ["onConfirmed"]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_18))
              ])
            ], 2 /* CLASS */))
          }), 128 /* KEYED_FRAGMENT */))
        ])
      ])
    ])
  ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
}
}

})